import React, { useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { alertActions, certificateActions } from '../../../store/actions'
import { addLifecycle, removeLifecycle } from '../../../assets/js/native.lifecycle'

/**
 * PCC 본인확인 V4 (모바일/모비어스 웹뷰) 컴포넌트.
 * PccMobile.jsx의 V4 병행 버전. 완료 시 crypto_token_id를 resultPccInfoV4로 전달.
 *
 * [중요] 파라미터 조회 시점 — PccV4.jsx와 동일하게 마운트가 아니라 본인인증 클릭(isPccOpen=true) 시점에 조회.
 *
 * props.memberId (옵션): 회원가입 흐름에서 입력한 아이디.
 */
const PccV4Mobile = (props) => {
  const M = window.M

  // PccV4.jsx와 동일한 오픈 사이클 가드 (armed/sawClear/opened). 상세 주석은 PccV4.jsx 참고.
  const cycleRef = useRef({ armed: false, sawClear: false, opened: false })

  // 마운트 시에는 웹뷰 라이프사이클 리스너만 등록 (파라미터 요청은 본인인증 클릭 시점으로 이동)
  useEffect(() => {
    // 초기화
    M.data.removeParam('crypto_token_id')

    const onRestoreEvent = () => {
      const cryptoTokenId = M.data.param('crypto_token_id')
      if (cryptoTokenId) {
        props.resultPccInfoV4(cryptoTokenId)
        M.data.removeParam('crypto_token_id')
      } else {
        props.onChange()
      }
    }

    addLifecycle('onrestore', onRestoreEvent)
    return () => {
      removeLifecycle('onrestore', onRestoreEvent)
    }
  }, [])

  // 본인인증 클릭(isPccOpen=true) 시점에: 이전 값 초기화 후, 그때의 memberId로 파라미터 요청.
  useEffect(() => {
    if (props.isPccOpen === true) {
      cycleRef.current = { armed: true, sawClear: false, opened: false }
      props.setInitialState() // reqInfo='' 로 초기화 → 이전(stale) 값으로 오픈되는 것 방지
      props.openPccRequestV4(props.memberId)
    } else {
      cycleRef.current = { armed: false, sawClear: false, opened: false }
    }
  }, [props.isPccOpen])

  // 새로 받은 reqInfo가 준비된 뒤에만 표준창 오픈.
  useEffect(() => {
    const c = cycleRef.current
    if (!c.armed || c.opened) return
    if (!props.pccInfo.reqInfo) {
      // 초기화(reqInfo='')가 반영된 것을 관측 → 이후 도착하는 값만 '새 값'으로 인정
      c.sawClear = true
      return
    }
    // 아직 초기화를 관측하지 못한 상태의 truthy 값은 이전 사이클의 잔존값이므로 무시
    if (!c.sawClear) return
    c.opened = true
    M.page.html({
      url: 'https://wallet.utradehub.or.kr/pccCertPopupMobileV4.htm',
      param: {
        verSion: props.pccInfo.verSion || '3',
        retUrl: props.pccInfo.retUrl,
        reqInfo: props.pccInfo.reqInfo,
        crypto_token_id: props.pccInfo.cryptoTokenId,
        integrity_value: props.pccInfo.integrityValue,
        standardWindowUrl: props.pccInfo.standardWindowUrl,
      },
      actionType: 'NO_HISTORY',
    })
  }, [props.isPccOpen, props.pccInfo.reqInfo])

  useEffect(() => {
    if (props.certResult === 'Y') {
      props.success({
        title: '확인',
        message: '본인인증이 정상 인증되었습니다.',
        params: {
          type: 'success',
          callback: () => {
            props.onChange()
            props.callBack && props.callBack(true)
          },
        },
      })
    } else if (props.certResult === 'N') {
      props.onChange()
      props.error({
        title: '확인',
        message: '본인인증을 실패했습니다.',
      })
    }
  }, [props.certResult])

  return (
    <React.Fragment>
      <form
        id="tranMgr"
        target="exWNStartViewElecDocActivity"
        name="PCCV3WFRM"
        method="post"
        action={props.pccInfo.standardWindowUrl || 'https://pcc.siren24.com/pcc_V3/jsp/pcc_V3_j10_v4.jsp'}
      >
        <input type="hidden" id="verSion" name="verSion" value={props.pccInfo.verSion || '3'} readOnly />
        <input type="hidden" id="retUrl" name="retUrl" value={props.pccInfo.retUrl} readOnly />
        <input type="hidden" id="reqInfo" name="reqInfo" value={props.pccInfo.reqInfo} readOnly />
        <input type="hidden" id="crypto_token_id" name="crypto_token_id" value={props.pccInfo.cryptoTokenId} readOnly />
        <input type="hidden" id="integrity_value" name="integrity_value" value={props.pccInfo.integrityValue} readOnly />
        <input type="hidden" id="retCryptoTokenId" name="retCryptoTokenId" />
      </form>
    </React.Fragment>
  )
}

const mapStateToProps = (state) => {
  return {
    auth: state.auth,
    pccInfo: state.certificate,
    certResult: state.certificate?.resultInfo?.result,
  }
}

const actionCreators = {
  openPccRequestV4: certificateActions.openPccRequestV4,
  resultPccInfoV4: certificateActions.resultPccInfoV4,
  setInitialState: certificateActions.setInitialState,
  success: alertActions.success,
  error: alertActions.error,
}

export default connect(mapStateToProps, actionCreators)(PccV4Mobile)
